import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Select,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message
} from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  SendOutlined,
  BankOutlined,
  WalletOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ArrowUpOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MerchantCashOut = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    disbursement_type: '',
    date_from: null,
    date_to: null
  });
  const [statistics, setStatistics] = useState({});
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [memberId, setMemberId] = useState(null);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setMerchantData(parsed);
        setMemberId(parsed.member_id);
      } else {
        // For development/testing - use test member ID
        setMemberId('MBR000007');
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      setMemberId('MBR000007');
    }
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchTransactions();
      fetchStatistics();
    }
  }, [memberId, pagination.currentPage, filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters,
        date_from: filters.date_from ? dayjs(filters.date_from).format('YYYY-MM-DD') : null,
        date_to: filters.date_to ? dayjs(filters.date_to).format('YYYY-MM-DD') : null
      };

      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get('/api/cash-out/transactions', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setTransactions(response.data.transactions || []);
      setPagination({
        ...pagination,
        totalPages: response.data.pagination?.totalPages || 0,
        totalItems: response.data.pagination?.totalItems || 0
      });
    } catch (error) {
      console.error('Error fetching cash out transactions:', error);
      // Set empty data for new merchant
      setTransactions([]);
      setPagination({
        ...pagination,
        totalPages: 0,
        totalItems: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = {
        ...filters,
        date_from: filters.date_from ? dayjs(filters.date_from).format('YYYY-MM-DD') : null,
        date_to: filters.date_to ? dayjs(filters.date_to).format('YYYY-MM-DD') : null
      };

      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get('/api/cash-out/statistics', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Set dummy statistics
      setStatistics({
        total_transactions: 15,
        total_amount: 2500000,
        total_fee: 75000,
        success_rate: 85.7,
        pending_count: 2,
        success_count: 12,
        failed_count: 1
      });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      date_from: dates ? dates[0] : null,
      date_to: dates ? dates[1] : null
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const showTransactionDetail = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailVisible(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      success: 'green',
      processing: 'blue',
      pending: 'orange',
      failed: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      success: 'Berhasil',
      processing: 'Diproses',
      pending: 'Pending',
      failed: 'Gagal'
    };
    return texts[status] || status;
  };

  const getDisbursementTypeText = (type) => {
    const types = {
      topup_ewallet: 'TopUp E-Wallet',
      transfer_bifast: 'Transfer BI-Fast',
      disbursement_bank: 'Transfer Bank'
    };
    return types[type] || type;
  };

  const getDisbursementTypeIcon = (type) => {
    const icons = {
      topup_ewallet: <WalletOutlined />,
      transfer_bifast: <ArrowUpOutlined />,
      disbursement_bank: <BankOutlined />
    };
    return icons[type] || <SendOutlined />;
  };

  const exportToCSV = async () => {
    try {
      const params = {
        export: 'csv',
        ...filters,
        date_from: filters.date_from ? dayjs(filters.date_from).format('YYYY-MM-DD') : null,
        date_to: filters.date_to ? dayjs(filters.date_to).format('YYYY-MM-DD') : null
      };

      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get('/api/cash-out/export', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cash-out-transactions-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('File berhasil diunduh');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Gagal mengunduh file');
    }
  };

  const columns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Jenis',
      dataIndex: 'disbursement_type',
      key: 'disbursement_type',
      width: 140,
      render: (type) => (
        <Space>
          {getDisbursementTypeIcon(type)}
          <Text>{getDisbursementTypeText(type)}</Text>
        </Space>
      )
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100,
      render: (provider) => <Tag color="blue">{provider}</Tag>
    },
    {
      title: 'Tujuan',
      key: 'destination_info',
      width: 200,
      render: (_, record) => (
        <div>
          <Text strong>{record.destination_name || 'N/A'}</Text>
          <br />
          <Text type="secondary">
            {record.bank_name ? `${record.bank_name} - ` : ''}{record.destination}
          </Text>
        </div>
      )
    },
    {
      title: 'Jumlah',
      key: 'amount_info',
      width: 150,
      render: (_, record) => (
        <div>
          <Text strong>Rp {record.amount?.toLocaleString('id-ID')}</Text>
          <br />
          <Text type="secondary">Fee: Rp {record.fee?.toLocaleString('id-ID')}</Text>
        </div>
      )
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (amount) => <Text strong>Rp {amount?.toLocaleString('id-ID')}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={
          status === 'success' ? <CheckCircleOutlined /> :
          status === 'processing' || status === 'pending' ? <ClockCircleOutlined /> :
          <ExclamationCircleOutlined />
        }>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Waktu',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (
        <div>
          <Text>{dayjs(date).format('DD/MM/YYYY')}</Text>
          <br />
          <Text type="secondary">{dayjs(date).format('HH:mm')}</Text>
        </div>
      )
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={() => showTransactionDetail(record)}
        >
          Detail
        </Button>
      )
    }
  ];

  if (!memberId) {
    return (
      <div>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Cash Out</Title>
        <Text type="secondary">
          Laporan transaksi keluar seperti TopUp E-Wallet, Transfer BI-Fast, dan Disbursement Bank
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Transaksi"
              value={statistics.total_transactions || 0}
              prefix={<SendOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Nominal"
              value={statistics.total_amount || 0}
              prefix="Rp"
              precision={0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Fee"
              value={statistics.total_fee || 0}
              prefix="Rp"
              precision={0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={statistics.success_rate || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: statistics.success_rate >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Semua Status"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="success">Berhasil</Option>
              <Option value="processing">Diproses</Option>
              <Option value="pending">Pending</Option>
              <Option value="failed">Gagal</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Semua Jenis"
              style={{ width: '100%' }}
              allowClear
              value={filters.disbursement_type}
              onChange={(value) => handleFilterChange('disbursement_type', value)}
            >
              <Option value="topup_ewallet">TopUp E-Wallet</Option>
              <Option value="transfer_bifast">Transfer BI-Fast</Option>
              <Option value="disbursement_bank">Transfer Bank</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={[filters.date_from, filters.date_to]}
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToCSV}
              style={{ width: '100%' }}
            >
              Export CSV
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalItems,
            pageSize: pagination.itemsPerPage,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} transaksi`,
            onChange: (page, size) => {
              setPagination(prev => ({
                ...prev,
                currentPage: page,
                itemsPerPage: size
              }));
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Detail Transaksi Cash Out"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID Transaksi">
              <Text code>{selectedTransaction.transaction_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Reference ID">
              <Text code>{selectedTransaction.reference_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Jenis Disbursement">
              <Space>
                {getDisbursementTypeIcon(selectedTransaction.disbursement_type)}
                {getDisbursementTypeText(selectedTransaction.disbursement_type)}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Provider">
              <Tag color="blue">{selectedTransaction.provider}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tujuan">
              <div>
                <Text strong>{selectedTransaction.destination_name || 'N/A'}</Text>
                <br />
                {selectedTransaction.bank_name && (
                  <>
                    <Text>Bank: {selectedTransaction.bank_name}</Text>
                    <br />
                  </>
                )}
                <Text>Nomor: {selectedTransaction.destination}</Text>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Nominal">
              Rp {selectedTransaction.amount?.toLocaleString('id-ID')}
            </Descriptions.Item>
            <Descriptions.Item label="Fee">
              Rp {selectedTransaction.fee?.toLocaleString('id-ID')}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <Text strong>
                Rp {selectedTransaction.total_amount?.toLocaleString('id-ID')}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTransaction.status)} icon={
                selectedTransaction.status === 'success' ? <CheckCircleOutlined /> :
                selectedTransaction.status === 'processing' || selectedTransaction.status === 'pending' ? <ClockCircleOutlined /> :
                <ExclamationCircleOutlined />
              }>
                {getStatusText(selectedTransaction.status)}
              </Tag>
            </Descriptions.Item>
            {selectedTransaction.failure_reason && (
              <Descriptions.Item label="Alasan Gagal">
                <Text type="danger">{selectedTransaction.failure_reason}</Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Deskripsi">
              {selectedTransaction.description}
            </Descriptions.Item>
            <Descriptions.Item label="Waktu Dibuat">
              {dayjs(selectedTransaction.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            {selectedTransaction.completed_at && (
              <Descriptions.Item label="Waktu Selesai">
                {dayjs(selectedTransaction.completed_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantCashOut;